import { Play, Music as MusicIcon } from "lucide-react";
import { useMusic } from "../lib/music";

/**
 * Horizontal row of "music drop" cards. Each click loads the track into the
 * global <MusicPlayerBar> at the bottom of the page. Layout: square album
 * art with a hover-revealed play overlay. Tracks are <Video> records where
 * `type === "music"`.
 */
export default function MusicRow({ accent = "Music", title = "Audio Drops", tracks = [], testid = "row-music" }) {
  const { play, track: current, playing } = useMusic();
  if (!tracks?.length) return null;

  const onPlay = (t) => {
    const src = t.playback_url || t.video_url || (t.video_path ? `/api/stream/${t.video_path}` : null);
    if (!src) return;
    const art = t.thumbnail || t.thumbnail_url || (t.thumbnail_path ? `/api/files/${t.thumbnail_path}` : null);
    play({
      id: t.id,
      title: t.title,
      artist: t.tags?.find?.((x) => typeof x === "string") || t.category || "TheSaiyanVictoria",
      src,
      art,
      mirrors: t.mirrors || {},
    });
  };

  return (
    <section data-testid={testid} className="px-4 sm:px-8 lg:px-20 mt-10">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-xs uppercase tracking-[0.3em] text-[#FFB800] font-black flex items-center gap-2">
          <MusicIcon className="w-3 h-3" /> {accent}
        </h2>
        <span className="text-[10px] uppercase tracking-widest text-zinc-600">{title}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {tracks.map((t) => {
          const isCurrent = current?.id === t.id;
          const art = t.thumbnail || t.thumbnail_url || (t.thumbnail_path ? `/api/files/${t.thumbnail_path}` : null);
          return (
            <button
              key={t.id}
              data-testid={`music-card-${t.id}`}
              onClick={() => onPlay(t)}
              className="group relative aspect-square rounded-lg overflow-hidden border border-white/5 bg-zinc-950 hover:border-[#FF6B00]/50 transition-colors text-left"
            >
              {art ? (
                <img
                  src={art}
                  alt={t.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/20 via-[#7A4F00]/20 to-black grid place-items-center">
                  <MusicIcon className="w-10 h-10 text-[#FFB800]/40" />
                </div>
              )}
              {/* Always-on bottom gradient + title */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-2.5 pt-10">
                <div className="text-xs font-bold text-white leading-tight line-clamp-2">{t.title}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5">{t.category || "Track"}</div>
              </div>
              {/* Play overlay — visible on hover OR when this is the current track */}
              <div
                className={`absolute inset-0 grid place-items-center transition-opacity duration-200 ${
                  isCurrent ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-full grid place-items-center shadow-2xl ${
                    isCurrent && playing ? "bg-[#FFB800] animate-pulse" : "bg-[#FF6B00]"
                  }`}
                >
                  <Play size={20} fill="#000" className="text-black ml-0.5" />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
